// src/pages/MaintenanceCalendarPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { addDays, isBefore, isToday } from 'date-fns';
import es from 'date-fns/locale/es';
import uk from 'date-fns/locale/uk';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  useTheme,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  CleaningServices as CleaningIcon,
  Info as InfoIcon,
  Add as AddIcon,
  PlayArrow as PlayArrowIcon,
  Schedule as ScheduleIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';

import { getData, saveData } from '../services/storageService';
import { useNotifications } from '../context/NotificationContext';
import { useI18n } from '../i18n/i18n';
import { MaintenanceScheduler } from '../services/maintenanceScheduler';

// Налаштування локалізації для date-fns
const locales = {
  'es': es,
  'uk': uk,
  'en': enUS,
  'ca': es,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const MaintenanceCalendarPage = () => {
  const theme = useTheme();
  const { showNotification } = useNotifications();
  const { t, lang } = useI18n();
  
  const [events, setEvents] = useState([]);
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState(Views.MONTH);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [filter, setFilter] = useState('all');
  
  const [scheduler] = useState(new MaintenanceScheduler());
  const [newTask, setNewTask] = useState({
    machineId: '',
    machineName: '',
    type: 'preventive',
    frequency: 'monthly',
    duration: 60,
    assignedTo: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Завантажуємо існуючі події
      const maintenanceData = await getData('maintenance_events') || [];
      
      // Завантажуємо автоматичні розклади
      await scheduler.loadData();
      const tasks = scheduler.getWeeklyTasks();
      setMaintenanceTasks(tasks);

      // Об'єднуємо події
      const calendarEvents = [
        ...maintenanceData
          .filter(event => event.nextServiceDate)
          .map(event => {
            const eventDate = new Date(event.nextServiceDate);
            const isOverdue = isBefore(eventDate, new Date()) && !isToday(eventDate);
            
            return {
              id: event.id,
              title: `${event.equipmentName} - ${event.eventType}`,
              start: eventDate,
              end: eventDate,
              allDay: true,
              resource: event,
              isOverdue,
              eventType: event.eventType,
              priority: event.priority || 'medium',
              source: 'legacy'
            };
          }),
        ...tasks.map(task => {
          const eventDate = new Date(task.nextDue);
          const isOverdue = isBefore(eventDate, new Date()) && !isToday(eventDate);
          
          return {
            id: task.id,
            title: `${task.machineName} - ${task.type === 'preventive' ? t('preventive_maintenance') : t('repair')}`,
            start: eventDate,
            end: eventDate,
            allDay: true,
            resource: task,
            isOverdue,
            eventType: task.type === 'preventive' ? t('preventive_maintenance') : t('repair'),
            priority: task.status === 'urgent' ? 'high' : 'medium',
            source: 'scheduler'
          };
        })
      ];
      
      setEvents(calendarEvents);
    } catch (error) {
      console.error("Error loading maintenance events:", error);
      showNotification(t('error_loading_events'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = useCallback((newDate) => setCurrentDate(newDate), []);
  const handleView = useCallback((newView) => setCurrentView(newView), []);
  
  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event);
    setEventDialogOpen(true);
  }, []);

  const handleCloseEventDialog = () => {
    setEventDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleCreateTask = async () => {
    try {
      await scheduler.createWorkOrder({
        title: `${newTask.machineName} - ${t(newTask.type)}`,
        description: t('scheduled_maintenance'),
        machineId: newTask.machineId,
        machineName: newTask.machineName,
        type: newTask.type,
        assignedTo: newTask.assignedTo || 'technician_team',
        dueDate: addDays(new Date(), 7).toISOString(),
        templateId: null
      });

      showNotification(t('task_created_success'), 'success');
      setCreateDialogOpen(false);
      setNewTask({
        machineId: '',
        machineName: '',
        type: 'preventive',
        frequency: 'monthly',
        duration: 60,
        assignedTo: ''
      });
      loadData();
    } catch (error) {
      showNotification(t('error_creating_task'), 'error');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await scheduler.markMaintenanceCompleted(taskId, 60, t('completed_via_calendar'));
      showNotification(t('task_completed_success'), 'success');
      loadData();
      setEventDialogOpen(false);
    } catch (error) {
      showNotification(t('error_completing_task'), 'error');
    }
  };

  const getEventStyle = useCallback((event) => {
    let backgroundColor = theme.palette.primary.main;
    let borderColor = theme.palette.primary.dark;

    if (event.isOverdue) {
      backgroundColor = theme.palette.error.main;
      borderColor = theme.palette.error.dark;
    } else {
      switch (event.eventType) {
        case t('preventive_maintenance'):
          backgroundColor = theme.palette.success.main;
          borderColor = theme.palette.success.dark;
          break;
        case t('repair'):
          backgroundColor = theme.palette.error.main;
          borderColor = theme.palette.error.dark;
          break;
        case t('cleaning'):
          backgroundColor = theme.palette.info.main;
          borderColor = theme.palette.info.dark;
          break;
        case t('diagnostics'):
          backgroundColor = theme.palette.warning.main;
          borderColor = theme.palette.warning.dark;
          break;
        default:
          backgroundColor = theme.palette.primary.main;
          borderColor = theme.palette.primary.dark;
      }
    }

    return {
      style: {
        backgroundColor,
        border: `2px solid ${borderColor}`,
        borderRadius: '4px',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '12px',
        padding: '2px 4px',
        cursor: 'pointer'
      }
    };
  }, [theme, t]);

  const messages = useMemo(() => ({
    allDay: t('all_day'),
    previous: t('previous'),
    next: t('next'),
    today: t('today'),
    month: t('month'),
    week: t('week'),
    day: t('day'),
    agenda: t('agenda'),
    date: t('date'),
    time: t('time'),
    event: t('event'),
    noEventsInRange: t('no_events_in_range'),
    showMore: total => `+ ${total} ${t('more')}`,
  }), [t]);

  const filteredEvents = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter(event => 
      event.eventType === filter || 
      (filter === 'overdue' && event.isOverdue)
    );
  }, [events, filter]);

  const eventTypes = useMemo(() => {
    const types = {};
    events.forEach(event => {
      types[event.eventType] = (types[event.eventType] || 0) + 1;
    });
    return types;
  }, [events]);

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case t('preventive_maintenance'): return <CheckCircleIcon />;
      case t('repair'): return <BuildIcon />;
      case t('cleaning'): return <CleaningIcon />;
      case t('diagnostics'): return <WarningIcon />;
      default: return <EventIcon />;
    }
  };

  const getEventTypeColor = (eventType) => {
    switch (eventType) {
      case t('preventive_maintenance'): return 'success';
      case t('repair'): return 'error';
      case t('cleaning'): return 'info';
      case t('diagnostics'): return 'warning';
      default: return 'primary';
    }
  };

  const stats = useMemo(() => {
    const overdueEvents = events.filter(event => event.isOverdue).length;
    const todayEvents = events.filter(event => isToday(new Date(event.start))).length;
    const weeklyEvents = events.filter(event => 
      isBefore(new Date(event.start), addDays(new Date(), 7)) && 
      !isBefore(new Date(event.start), new Date())
    ).length;

    return {
      total: events.length,
      overdue: overdueEvents,
      today: todayEvents,
      weekly: weeklyEvents,
      completionRate: scheduler.getMaintenanceStats().completionRate
    };
  }, [events, scheduler]);

  const CustomToolbar = ({ onNavigate, label }) => (
    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
      <Button variant="outlined" onClick={() => onNavigate('PREV')}>
        {t('previous')}
      </Button>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Button variant="outlined" onClick={() => onNavigate('NEXT')}>
        {t('next')}
      </Button>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Заголовок */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: theme.palette.primary.main,
            mx: 'auto',
            mb: 2
          }}
        >
          <CalendarIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {t('maintenance_calendar')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('maintenance_scheduling_tracking')}
        </Typography>
      </Box>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('total_events')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h3" color="error.main" sx={{ fontWeight: 700 }}>
                {stats.overdue}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('overdue_events')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                {stats.today}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('today_events')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                {Math.round(stats.completionRate)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('completion_rate')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Панель керування */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab icon={<CalendarIcon />} label={t('calendar_view')} />
              <Tab icon={<ScheduleIcon />} label={t('task_list')} />
            </Tabs>

            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>{t('filter')}</InputLabel>
                <Select
                  value={filter}
                  label={t('filter')}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('all_events')}</MenuItem>
                  <MenuItem value="overdue">{t('overdue')}</MenuItem>
                  <MenuItem value={t('preventive_maintenance')}>{t('preventive_maintenance')}</MenuItem>
                  <MenuItem value={t('repair')}>{t('repair')}</MenuItem>
                  <MenuItem value={t('cleaning')}>{t('cleaning')}</MenuItem>
                </Select>
              </FormControl>

              <Tooltip title={t('add_new_task')}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                >
                  {t('new_task')}
                </Button>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Легенда типів подій */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            {t('event_legend')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {Object.entries(eventTypes).map(([eventType, count]) => (
              <Chip
                key={eventType}
                icon={getEventTypeIcon(eventType)}
                label={`${eventType}: ${count}`}
                color={getEventTypeColor(eventType)}
                variant="outlined"
              />
            ))}
            {stats.overdue > 0 && (
              <Chip
                icon={<WarningIcon />}
                label={`${t('overdue')}: ${stats.overdue}`}
                color="error"
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Вміст залежно від активної вкладки */}
      {activeTab === 0 ? (
        /* Календар */
        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ height: '70vh' }}>
              <Calendar
                localizer={localizer}
                events={filteredEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                date={currentDate}
                view={currentView}
                onNavigate={handleNavigate}
                onView={handleView}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={getEventStyle}
                messages={messages}
                popup
                tooltipAccessor={null}
                views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
                components={{
                  toolbar: CustomToolbar
                }}
              />
            </Box>
          </CardContent>
        </Card>
      ) : (
        /* Список завдань */
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {t('upcoming_tasks')} ({scheduler.getWeeklyTasks().length})
            </Typography>
            <List>
              {scheduler.getWeeklyTasks().map((task) => (
                <ListItem 
                  key={task.id}
                  secondaryAction={
                    <Tooltip title={t('start_task')}>
                      <IconButton 
                        edge="end"
                        onClick={() => handleCompleteTask(task.id)}
                        color="primary"
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                  }
                  sx={{
                    borderLeft: 4,
                    borderColor: task.status === 'urgent' ? 'error.main' : 
                                isBefore(new Date(task.nextDue), new Date()) ? 'error.main' : 'warning.main'
                  }}
                >
                  <ListItemIcon>
                    <Badge 
                      color="error" 
                      variant="dot" 
                      invisible={!isBefore(new Date(task.nextDue), new Date())}
                    >
                      <BuildIcon color="action" />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {task.machineName}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(task.nextDue).toLocaleDateString()} • {task.duration} {t('minutes')}
                        </Typography>
                        <Chip
                          label={task.type === 'preventive' ? t('preventive_maintenance') : t('repair')}
                          size="small"
                          color={task.type === 'preventive' ? 'success' : 'error'}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Діалог деталей події */}
      <Dialog 
        open={eventDialogOpen} 
        onClose={handleCloseEventDialog}
        maxWidth="sm"
        fullWidth
      >
        {selectedEvent && (
          <>
            <DialogTitle sx={{ fontWeight: 600 }}>
              {t('event_details')}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                      <BuildIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {selectedEvent.resource.equipmentName || selectedEvent.resource.machineName}
                      </Typography>
                      <Chip
                        icon={getEventTypeIcon(selectedEvent.eventType)}
                        label={selectedEvent.eventType}
                        color={getEventTypeColor(selectedEvent.eventType)}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('scheduled_date')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedEvent.start.toLocaleDateString(lang, { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('status')}
                  </Typography>
                  <Chip
                    label={selectedEvent.isOverdue ? t('overdue') : t('pending')}
                    color={selectedEvent.isOverdue ? 'error' : 'success'}
                    size="small"
                  />
                </Grid>

                {selectedEvent.resource.assignedTo && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('assigned_technician')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedEvent.resource.assignedTo}
                    </Typography>
                  </Grid>
                )}

                {selectedEvent.resource.duration && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('estimated_duration')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedEvent.resource.duration} {t('minutes')}
                    </Typography>
                  </Grid>
                )}

                {selectedEvent.source === 'scheduler' && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => handleCompleteTask(selectedEvent.resource.id)}
                      fullWidth
                    >
                      {t('start_task')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEventDialog} sx={{ borderRadius: 2 }}>
                {t('close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Діалог створення нового завдання */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t('create_new_task')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label={t('machine_name')}
                value={newTask.machineName}
                onChange={(e) => setNewTask({...newTask, machineName: e.target.value})}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('machine_id')}
                value={newTask.machineId}
                onChange={(e) => setNewTask({...newTask, machineId: e.target.value})}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('task_type')}</InputLabel>
                <Select
                  value={newTask.type}
                  label={t('task_type')}
                  onChange={(e) => setNewTask({...newTask, type: e.target.value})}
                >
                  <MenuItem value="preventive">{t('preventive_maintenance')}</MenuItem>
                  <MenuItem value="repair">{t('repair')}</MenuItem>
                  <MenuItem value="cleaning">{t('cleaning')}</MenuItem>
                  <MenuItem value="diagnostics">{t('diagnostics')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('duration_minutes')}
                type="number"
                value={newTask.duration}
                onChange={(e) => setNewTask({...newTask, duration: parseInt(e.target.value) || 60})}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleCreateTask} 
            variant="contained"
            disabled={!newTask.machineName || !newTask.machineId}
          >
            {t('create_task')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceCalendarPage;